
import { UserRole } from '@/types/userRole';

// Define permission structure and map roles to permissions
export const PERMISSIONS = {
  // Campaign permissions
  VIEW_CAMPAIGNS: 'view:campaigns',
  CREATE_CAMPAIGNS: 'create:campaigns',
  EDIT_CAMPAIGNS: 'edit:campaigns',
  DELETE_CAMPAIGNS: 'delete:campaigns',
  
  // User management permissions
  VIEW_USERS: 'view:users',
  INVITE_USERS: 'invite:users',
  DELETE_USERS: 'delete:users',
  EDIT_USERS: 'edit:users',
  
  // Settings permissions
  VIEW_SETTINGS: 'view:settings',
  EDIT_SETTINGS: 'edit:settings',
  
  // Analytics permissions
  VIEW_ANALYTICS: 'view:analytics',
  EXPORT_ANALYTICS: 'export:analytics',
  
  // Role management permissions
  VIEW_ROLES: 'view:roles',
  MANAGE_ROLES: 'manage:roles',
  ASSIGN_ROLES: 'assign:roles',
  
  // Feature permissions
  MANAGE_FRAUD_DETECTION: 'manage:fraud_detection',
  MANAGE_EMAIL_NOTIFICATIONS: 'manage:email_notifications',
  MANAGE_INTEGRATIONS: 'manage:integrations',
  MANAGE_ACCESS_CONTROL: 'manage:access_control',
  CUSTOMIZE_DESIGN: 'customize:design',
  MANAGE_EMAIL_DOMAIN: 'manage:email_domain',
  MANAGE_AUTO_REWARD: 'manage:auto_reward',
  MANAGE_AUTO_SIGNUP: 'manage:auto_signup',
  MANAGE_WEBHOOKS: 'manage:webhooks',
  MANAGE_WHITE_LABEL: 'manage:white_label',
  
  // Master license specific permissions
  MANAGE_LICENSES: 'manage:licenses',
  VIEW_SUPER_ADMINS: 'view:super_admins',
  MANAGE_SUPER_ADMINS: 'manage:super_admins',
  MANAGE_DOMAINS: 'manage:domains',
  MANAGE_BILLING: 'manage:billing',
  VIEW_SYSTEM_LOGS: 'view:system_logs',
  MANAGE_SYSTEM_SETTINGS: 'manage:system_settings',
  
  // Reseller specific permissions
  MANAGE_RESELLER_PLANS: 'manage:reseller_plans',
  VIEW_RESELLER_SALES: 'view:reseller_sales',
  MANAGE_RESELLER_CRM: 'manage:reseller_crm',
  MANAGE_REFERRAL_BANK: 'manage:referral_bank',
  
  // Support permissions
  VIEW_SUPPORT: 'view:support',
  MANAGE_SUPPORT: 'manage:support',
  MANAGE_KNOWLEDGE_BASE: 'manage:knowledge_base',
};

// Define role-based permissions
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  // The masterAdmin role has all permissions
  masterAdmin: Object.values(PERMISSIONS),
  
  // Super admin has most permissions but not some master-specific ones
  superadmin: [
    ...Object.values(PERMISSIONS).filter(p => 
      !p.includes('manage:super_admins') && 
      !p.includes('manage:licenses') &&
      !p.includes('manage:billing') &&
      !p.includes('manage:system_settings')
    )
  ],
  
  // Regular admin permissions
  admin: [
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.CREATE_CAMPAIGNS,
    PERMISSIONS.EDIT_CAMPAIGNS,
    PERMISSIONS.DELETE_CAMPAIGNS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.INVITE_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_ANALYTICS,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.EDIT_SETTINGS,
    PERMISSIONS.CUSTOMIZE_DESIGN,
    PERMISSIONS.MANAGE_EMAIL_NOTIFICATIONS,
    PERMISSIONS.MANAGE_AUTO_REWARD,
    PERMISSIONS.MANAGE_AUTO_SIGNUP,
    PERMISSIONS.VIEW_SUPPORT,
  ],
  
  // Manager permissions
  manager: [
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.CREATE_CAMPAIGNS,
    PERMISSIONS.EDIT_CAMPAIGNS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.INVITE_USERS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_SETTINGS,
  ],
  
  // Regular user permissions
  user: [
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.VIEW_ANALYTICS,
  ],
  
  // Reseller permissions
  reseller: [
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.CREATE_CAMPAIGNS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.MANAGE_WHITE_LABEL,
    PERMISSIONS.CUSTOMIZE_DESIGN,
    PERMISSIONS.VIEW_RESELLER_SALES,
    PERMISSIONS.MANAGE_RESELLER_CRM,
  ],
  
  // Business permissions
  business: [
    PERMISSIONS.VIEW_CAMPAIGNS,
    PERMISSIONS.CREATE_CAMPAIGNS,
    PERMISSIONS.EDIT_CAMPAIGNS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.INVITE_USERS,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_SETTINGS,
    PERMISSIONS.CUSTOMIZE_DESIGN,
  ],
};

// Check if a role has a specific permission
export const hasPermission = (role: UserRole | undefined, permission: string): boolean => {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) || false;
};

// Check if a user has access to a specific feature based on multiple permissions
export const hasAccess = (role: UserRole | undefined, permissions: string[]): boolean => {
  if (!role) return false;
  return permissions.some(permission => hasPermission(role, permission));
};

// Check if a user has access to a specific feature based on their individual permissions
export const hasUserPermission = (userPermissions: string[] | undefined, permission: string): boolean => {
  if (!userPermissions || userPermissions.length === 0) return false;
  return userPermissions.includes(permission);
};

// Get all permissions for a role
export const getRolePermissions = (role: UserRole): string[] => {
  return ROLE_PERMISSIONS[role] || [];
};

// Get permissions by category
export const getPermissionsByCategory = (permissions: string[]): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  
  permissions.forEach(permission => {
    const [action, resource] = permission.split(':');
    if (!result[resource]) {
      result[resource] = [];
    }
    result[resource].push(permission);
  });
  
  return result;
};

// Check if the user has admin-level permissions
export const hasAdminPermissions = (role: UserRole | undefined): boolean => {
  if (!role) return false;
  return ['admin', 'superadmin', 'masterAdmin'].includes(role);
};
